import React, { useState, useEffect, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { EventInput, EventClickArg } from '@fullcalendar/core';
import { useDeleteCalendarEntry, useGetCalendarEntries, useGetCalendarEntry, useUpdateCalendarEntry } from "../hooks/calendarEntry";
import deLocale from '@fullcalendar/core/locales/de';
import {
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    FormControlLabel,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography,
    useTheme,
    useMediaQuery,
    Box,
    Tooltip,
    IconButton,
    Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import CalendarEventEdit from "./CalendarEventEdit";
import { ICalendarEntry } from "../Interfaces/ICalendarEntry";
import { checkPermission } from "../utils/permission";
import { useDetailRoute } from '../hooks/useDetailRoute';
import { useAlert } from '../core/AlertContext';
import PageHeader from '../core/PageHeader';
import { useToggleCalendarEntryFavorite } from '../hooks/calendarEntry';
import { useGetTags } from '../hooks/tags';
import TagChip from '../core/TagChip';
import { GREEN_700, GREEN_800, GREEN_950, GOLD_SOFT, GOLD_MUTED, STATUS_TONES, primaryButtonSx } from '../core/pvtaStyles';

/** Kalender-Typ-Akzente im PVTA-Branding: Events grün, Escheck/Buchung gold. */
const CAL_EVENT_BG = 'var(--pvta-green-100)';
const CAL_EVENT_ACCENT = GREEN_700;
const CAL_BOOKING_BG = GOLD_SOFT;
const CAL_BOOKING_ACCENT = GOLD_MUTED;

const formatDateForInput = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const formatTimeForInput = (date: Date): string =>
    `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const formatLocalDateTime = (date: Date): string =>
    `${formatDateForInput(date)} ${formatTimeForInput(date)}:00`;

const parseLocalDateTime = (str: string): Date => {
    if (str.includes("T")) return new Date(str);
    const [d, t] = str.split(" ");
    const [y, m, day] = d.split("-").map(Number);
    const [h = 0, min = 0, s = "0"] = (t || "00:00:00").split(":");
    return new Date(y, m - 1, day, Number(h), Number(min), Number(s.split(".")[0]));
};

const isSameDay = (left: Date, right: Date): boolean =>
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();

const hasMeaningfulTime = (date: Date): boolean => date.getHours() !== 0 || date.getMinutes() !== 0;

const formatMobileEventDate = (start: Date, end: Date): string => {
    if (isSameDay(start, end)) {
        return start.toLocaleDateString('de-DE', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }

    const startLabel = start.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
    });
    const endLabel = end.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
    return `${startLabel} – ${endLabel}`;
};

const formatMobileEventTime = (entry: ICalendarEntry, start: Date, end: Date): string | null => {
    if (entry.all_day) {
        return 'Ganztägig';
    }

    const showTime = hasMeaningfulTime(start) || hasMeaningfulTime(end);
    if (!showTime) {
        return 'Ganztägig';
    }

    const startTime = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    const endTime = end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return `${startTime} – ${endTime}`;
};

type TimeframeFilter = 'all' | 'week' | 'month';

type EventCounts = {
    going: number | null;
    notGoing: number | null;
    maybe: number | null;
    comments: number | null;
    media: number | null;
};

const extractEventCounts = (entry: ICalendarEntry): EventCounts => ({
    going: entry.counts?.rsvp.going ?? null,
    notGoing: entry.counts?.rsvp.not_going ?? null,
    maybe: entry.counts?.rsvp.maybe ?? null,
    comments: entry.counts?.comments ?? null,
    media: entry.counts?.media ?? null,
});

const RSVP_CHIP_SX = {
    going: {
        backgroundColor: STATUS_TONES.success.bg,
        borderColor: STATUS_TONES.success.border,
    },
    notGoing: {
        backgroundColor: STATUS_TONES.danger.bg,
        borderColor: STATUS_TONES.danger.border,
    },
    maybe: {
        backgroundColor: STATUS_TONES.warning.bg,
        borderColor: STATUS_TONES.warning.border,
    },
};

const getStartOfWeek = (date: Date): Date => {
    const day = date.getDay();
    const mondayDelta = day === 0 ? -6 : 1 - day;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + mondayDelta, 0, 0, 0, 0);
};

const getEndOfWeek = (date: Date): Date => {
    const startOfWeek = getStartOfWeek(date);
    return new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + 6, 23, 59, 59, 999);
};

const getStartOfMonth = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);

const getEndOfMonth = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

const overlapsRange = (entryStart: Date, entryEnd: Date, rangeStart: Date, rangeEnd: Date): boolean =>
    entryStart <= rangeEnd && entryEnd >= rangeStart;

const BOOKING_TITLE_SPLIT_PATTERN = /\s+[–-]\s+/;

const splitBookingTitleForMobile = (title: string): { primary: string; secondary: string | null } => {
    const parts = title.split(BOOKING_TITLE_SPLIT_PATTERN);
    if (parts.length < 2) {
        return { primary: title, secondary: null };
    }

    const primary = parts[0]?.trim() ?? title;
    const secondary = parts.slice(1).join(' - ').trim();
    if (!secondary || !/zimmer/i.test(secondary)) {
        return { primary: title, secondary: null };
    }

    return { primary, secondary };
};

interface CalendarProps {
    calendarType: 'event' | 'booking';
}

/** Basispfad je Kalendertyp — zugleich das Ziel beim Schliessen des Dialogs. */
const BASE_PATH: Record<CalendarProps['calendarType'], string> = {
    event: '/events',
    booking: '/bookings',
};

const Calendar: React.FC<CalendarProps> = ({ calendarType }) => {
    const [events, setEvents] = useState<EventInput[]>([]);
    const { calendarEntries, isLoadingCalendarEntries } = useGetCalendarEntries();
    const { showAlert } = useAlert();
    const basePath = BASE_PATH[calendarType];
    const { detailId, hasInvalidId, openDetail, closeDetail } = useDetailRoute(basePath);
    // Nur fuer noch nicht gespeicherte Eintraege: die haben keine ID und damit keine URL.
    const [draftEntry, setDraftEntry] = useState<ICalendarEntry | null>(null);
    const [searchInput, setSearchInput] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [timeframeFilter, setTimeframeFilter] = useState<TimeframeFilter>('all');
    const [showPastMobile, setShowPastMobile] = useState<boolean>(false);
    const [showOnlyFavorites, setShowOnlyFavorites] = useState<boolean>(false);
    const [activeTagId, setActiveTagId] = useState<number | null>(null);
    const { tags } = useGetTags();
    const updateCalendarEntry = useUpdateCalendarEntry();
    const deleteCalendarEntry = useDeleteCalendarEntry();
    const toggleFavoriteMutation = useToggleCalendarEntryFavorite();
    // Eintrag aus der bereits geladenen Liste als Startwert, damit sich der Dialog
    // beim Klick sofort fuellt; der Detail-Endpunkt liefert danach die vollen Daten
    // und traegt beim Deep-Link (Liste noch leer) die Anzeige allein.
    const listEntry = useMemo(
        () => (detailId === null ? null : calendarEntries.find((entry) => entry.id === detailId) ?? null),
        [calendarEntries, detailId],
    );
    const {
        calendarEntry: detailedSelectedEntry,
        isFetchedCalendarEntry,
        isErrorCalendarEntry,
    } = useGetCalendarEntry(detailId ?? undefined, listEntry);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const filteredEntries = useMemo(
        () => calendarEntries.filter(e => e.calendar_type === calendarType),
        [calendarEntries, calendarType]
    );

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearch(searchInput.trim().toLowerCase());
        }, 250);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [searchInput]);

    const handleEventClick = (info: EventClickArg) => {
        const id = Number(info.event.id);
        if (calendarEntries.some(e => e.id === id)) {
            openDetail(id);
        }
    };

    const handleDateClick = (arg: DateClickArg) => {
        if (!checkPermission(1, 2)) return;

        // Eine offene Detail-Route mitnehmen, sonst taucht der alte Eintrag nach dem
        // Abbrechen wieder auf.
        if (detailId !== null) {
            closeDetail();
        }
        setDraftEntry({
            id: undefined,
            title: '',
            description: null,
            start_time: formatLocalDateTime(new Date(arg.date)),
            end_time: formatLocalDateTime(new Date(arg.date)),
            all_day: false,
            location: null,
            calendar_type: calendarType,
            is_favorite: false,
        });
    };

    const timeframeEntries = useMemo(() => {
        const now = new Date();
        const weekStart = getStartOfWeek(now);
        const weekEnd = getEndOfWeek(now);
        const monthStart = getStartOfMonth(now);
        const monthEnd = getEndOfMonth(now);

        return filteredEntries
            .filter((entry) => {
                const start = parseLocalDateTime(entry.start_time);
                const end = parseLocalDateTime(entry.end_time);

                if (timeframeFilter === 'week') {
                    return overlapsRange(start, end, weekStart, weekEnd);
                }

                if (timeframeFilter === 'month') {
                    return overlapsRange(start, end, monthStart, monthEnd);
                }

                return true;
            })
            .filter((entry) => {
                if (showOnlyFavorites && !entry.is_favorite) {
                    return false;
                }

                if (!debouncedSearch) {
                    return true;
                }

                const haystack = [
                    entry.title,
                    entry.description ?? '',
                    entry.location ?? '',
                ]
                    .join(' ')
                    .toLowerCase();

                return haystack.includes(debouncedSearch);
            })
            .filter((entry) => (
                activeTagId === null || (entry.tags ?? []).some((tag) => tag.id === activeTagId)
            ))
            .sort((a, b) => (
                Number(Boolean(b.is_favorite)) - Number(Boolean(a.is_favorite))
                || (parseLocalDateTime(a.start_time).getTime() - parseLocalDateTime(b.start_time).getTime())
            ));
    }, [activeTagId, debouncedSearch, filteredEntries, showOnlyFavorites, timeframeFilter]);

    /**
     * Schliesst den Dialog: verwirft einen Entwurf und geht sonst auf die Uebersicht.
     * Absolut statt `navigate(-1)` — bei einem Link aus einer Mail gibt es keinen
     * App-Verlauf, ein Schritt zurueck wuerde die Anwendung verlassen.
     */
    const handleCloseDialog = (): void => {
        if (draftEntry) {
            setDraftEntry(null);
            return;
        }
        closeDetail();
    };

    const handleToggleFavorite = async (eventId: number, isFavorite: boolean): Promise<void> => {
        // Kein lokaler Nachzug noetig: die Mutation schreibt das Ergebnis direkt in die
        // Caches ['calendarEntries'] und ['calendarEntry', id], aus denen der Dialog liest.
        await toggleFavoriteMutation.mutateAsync({ eventId, isFavorite });
    };

    const mobileVisibleEntries = useMemo(() => {
        if (showPastMobile) {
            return timeframeEntries;
        }

        const now = Date.now();
        return timeframeEntries.filter((entry) => parseLocalDateTime(entry.end_time).getTime() >= now);
    }, [showPastMobile, timeframeEntries]);

    const mobileUpcomingEntries = useMemo(
        () => mobileVisibleEntries.filter((entry) => parseLocalDateTime(entry.end_time).getTime() >= Date.now()),
        [mobileVisibleEntries]
    );

    const mobilePastEntries = useMemo(
        () => (
            mobileVisibleEntries
                .filter((entry) => parseLocalDateTime(entry.end_time).getTime() < Date.now())
                .sort((a, b) => parseLocalDateTime(b.start_time).getTime() - parseLocalDateTime(a.start_time).getTime())
        ),
        [mobileVisibleEntries]
    );

    const dialogEntry = draftEntry ?? (detailId === null ? null : (detailedSelectedEntry ?? listEntry));

    // Deep-Link auf eine ID, die es nicht (mehr) gibt oder die der Nutzer nicht sehen
    // darf: zurueck auf die Uebersicht statt einer leeren Seite.
    const detailUnavailable = detailId !== null
        && (isErrorCalendarEntry || (isFetchedCalendarEntry && detailedSelectedEntry === null && listEntry === null));

    useEffect(() => {
        if (!hasInvalidId && !detailUnavailable) {
            return;
        }
        showAlert({
            severity: 'error',
            text: calendarType === 'event'
                ? 'Diese Veranstaltung existiert nicht oder ist nicht für dich freigegeben.'
                : 'Dieser Kalendereintrag existiert nicht oder ist nicht für dich freigegeben.',
        });
        closeDetail();
    }, [calendarType, closeDetail, detailUnavailable, hasInvalidId, showAlert]);

    useEffect(() => {
        const formatted = timeframeEntries.map((entry) => {
            const start = parseLocalDateTime(entry.start_time);
            let end = parseLocalDateTime(entry.end_time);

            if (entry.all_day) {
                end = new Date(end.getTime() + 86400000);
            }

            return {
                id: entry.id?.toString(),
                title: entry.title,
                start,
                end,
                allDay: entry.all_day,
                backgroundColor: entry.calendar_type === 'event' ? CAL_EVENT_BG : CAL_BOOKING_BG,
                borderColor: entry.calendar_type === 'event' ? CAL_EVENT_ACCENT : CAL_BOOKING_ACCENT,
                textColor: GREEN_950,
                extendedProps: {
                    description: entry.description,
                    location: entry.location,
                },
            };
        });
        setEvents(formatted);
    }, [timeframeEntries]);

    if (isLoadingCalendarEntries) {
        return (
            <Box sx={{ py: { xs: 3, md: 4 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <Container maxWidth={isMobile ? false : 'xl'} disableGutters={isMobile}>
                    <Box
                        sx={{
                            px: { xs: 1, sm: 2, md: 0 },
                            minWidth: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Stack spacing={1.25} sx={{ mb: 2 }}>
                            <Skeleton variant="text" width={220} height={44} />
                            <Skeleton variant="rounded" height={40} />
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {[0, 1, 2].map((item) => (
                                    <Skeleton key={item} variant="rounded" width={96} height={32} />
                                ))}
                            </Stack>
                            {isMobile && <Skeleton variant="text" width={180} />}
                        </Stack>
                        {isMobile ? (
                            <Stack spacing={1.5}>
                                {[0, 1, 2].map((item) => (
                                    <Card key={item} variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardContent sx={{ p: 1.5 }}>
                                            <Skeleton variant="text" width="55%" height={28} />
                                            <Skeleton variant="text" width="85%" />
                                            <Skeleton variant="text" width="45%" />
                                            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                                                <Skeleton variant="rounded" width={58} height={24} />
                                                <Skeleton variant="rounded" width={58} height={24} />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            <Box
                                sx={{
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: '0px 16px 32px rgba(15, 23, 42, 0.08)',
                                    p: { xs: 1, md: 2 },
                                }}
                            >
                                <Skeleton variant="rounded" height={720} />
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        );
    }

    const actionLabel = calendarType === 'event' ? 'Neue Veranstaltung' : 'Neue Buchung';

    return (
        <Box sx={{ py: { xs: 3, md: 4 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <Container maxWidth={isMobile ? false : 'xl'} disableGutters={isMobile}>
                <Box
                    sx={{
                        px: { xs: 1, sm: 2, md: 0 },
                        minWidth: 0,
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                    }}
                >
                    <PageHeader
                        title={calendarType === 'event' ? 'Veranstaltungen' : 'Escheck'}
                        subtitle={calendarType === 'event'
                            ? 'Termine und Veranstaltungen der Verbindung.'
                            : 'Buchungen und Belegung des Verbindungshauses.'}
                        action={
                            checkPermission(1, 2) ? (
                                isMobile ? (
                                    <Tooltip title={actionLabel}>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleDateClick({
                                                date: new Date(),
                                                dateStr: new Date().toISOString(),
                                                allDay: true,
                                            } as DateClickArg)}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        sx={{ ...primaryButtonSx }}
                                        onClick={() => handleDateClick({
                                            date: new Date(),
                                            dateStr: new Date().toISOString(),
                                            allDay: true,
                                        } as DateClickArg)}
                                    >
                                        {actionLabel}
                                    </Button>
                                )
                            ) : undefined
                        }
                    />

                    <Stack spacing={1.25} sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            fullWidth
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Suche nach Titel, Beschreibung oder Ort"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                label="Alle"
                                size="small"
                                color={timeframeFilter === 'all' ? 'primary' : 'default'}
                                onClick={() => setTimeframeFilter('all')}
                            />
                            <Chip
                                label="Diese Woche"
                                size="small"
                                color={timeframeFilter === 'week' ? 'primary' : 'default'}
                                onClick={() => setTimeframeFilter('week')}
                            />
                            <Chip
                                label="Dieser Monat"
                                size="small"
                                color={timeframeFilter === 'month' ? 'primary' : 'default'}
                                onClick={() => setTimeframeFilter('month')}
                            />
                            {calendarType === 'event' && (
                                <Chip
                                    label="Nur Favoriten"
                                    size="small"
                                    color={showOnlyFavorites ? 'primary' : 'default'}
                                    onClick={() => setShowOnlyFavorites((current) => !current)}
                                />
                            )}
                        </Stack>
                        {calendarType === 'event' && tags.length > 0 && (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                                <Chip
                                    label="Alle Tags"
                                    size="small"
                                    onClick={() => setActiveTagId(null)}
                                    sx={{
                                        fontWeight: 600,
                                        backgroundColor: activeTagId === null ? GREEN_700 : 'var(--pvta-green-100)',
                                        color: activeTagId === null ? '#ffffff' : 'var(--pvta-text)',
                                        border: '1px solid',
                                        borderColor: activeTagId === null ? GREEN_700 : 'var(--pvta-border)',
                                        '&:hover': { backgroundColor: activeTagId === null ? GREEN_800 : 'var(--pvta-green-100)' },
                                    }}
                                />
                                {tags.map((tag) => {
                                    const active = activeTagId === tag.id;
                                    return (
                                        <Chip
                                            key={tag.id}
                                            label={tag.name}
                                            size="small"
                                            onClick={() => setActiveTagId(active ? null : tag.id)}
                                            sx={{
                                                fontWeight: 600,
                                                backgroundColor: active ? tag.color : 'var(--pvta-green-100)',
                                                color: active ? '#ffffff' : 'var(--pvta-text)',
                                                border: '1px solid',
                                                borderColor: active ? tag.color : 'var(--pvta-border)',
                                                '&:hover': { backgroundColor: active ? tag.color : 'var(--pvta-green-100)' },
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        )}
                        {isMobile && (
                            <FormControlLabel
                                control={(
                                    <Switch
                                        size="small"
                                        checked={showPastMobile}
                                        onChange={(event) => setShowPastMobile(event.target.checked)}
                                    />
                                )}
                                label="Vergangene anzeigen"
                                sx={{ ml: 0.25, mr: 0, alignSelf: 'flex-start' }}
                            />
                        )}
                    </Stack>

                    {isMobile && mobileVisibleEntries.length === 0 && (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px dashed',
                                borderColor: 'divider',
                                backgroundColor: '#fbfaf8',
                            }}
                        >
                            <Typography color="text.secondary">Keine passenden Einträge.</Typography>
                        </Box>
                    )}

                    {isMobile && mobileVisibleEntries.length > 0 && (
                        <Stack spacing={1.5}>
                            {mobileUpcomingEntries.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Kommende Termine
                                </Typography>
                            )}
                            {mobileUpcomingEntries.map((entry) => {
                                const start = parseLocalDateTime(entry.start_time);
                                const end = parseLocalDateTime(entry.end_time);
                                const dateLabel = formatMobileEventDate(start, end);
                                const timeLabel = formatMobileEventTime(entry, start, end);
                                const counts = extractEventCounts(entry);
                                const bookingTitleParts = splitBookingTitleForMobile(entry.title);

                                return (
                                    <Card
                                        key={entry.id}
                                        variant="outlined"
                                        sx={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            minWidth: 0,
                                            borderRadius: 2,
                                            borderLeft: '4px solid',
                                            borderLeftColor: entry.calendar_type === 'event' ? CAL_EVENT_ACCENT : CAL_BOOKING_ACCENT,
                                            boxShadow: '0px 10px 26px rgba(15, 23, 42, 0.08)',
                                        }}
                                    >
                                        <CardContent
                                            sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, cursor: 'pointer', minWidth: 0 }}
                                            onClick={() => entry.id !== undefined && openDetail(entry.id)}
                                        >
                                            {entry.calendar_type === 'booking' && bookingTitleParts.secondary ? (
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 700,
                                                            minWidth: 0,
                                                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {bookingTitleParts.primary}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            minWidth: 0,
                                                            mt: 0.125,
                                                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {bookingTitleParts.secondary}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                                    <Typography
                                                        variant="subtitle1"
                                                        noWrap={!isMobile}
                                                        sx={{
                                                            fontWeight: 700,
                                                            minWidth: 0,
                                                            flex: 1,
                                                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: { xs: '-webkit-box', md: 'block' },
                                                            WebkitLineClamp: { xs: 2, md: 'unset' },
                                                            WebkitBoxOrient: { xs: 'vertical', md: 'unset' },
                                                        }}
                                                    >
                                                        {entry.title}
                                                    </Typography>
                                                    {entry.calendar_type === 'event' && (
                                                        <IconButton
                                                            size="small"
                                                            aria-label={entry.is_favorite ? 'aus Favoriten entfernen' : 'zu Favoriten hinzufügen'}
                                                            onClick={(eventClick) => {
                                                                eventClick.stopPropagation();
                                                                void handleToggleFavorite(entry.id as number, !Boolean(entry.is_favorite));
                                                            }}
                                                            sx={{ color: entry.is_favorite ? 'var(--pvta-favorite)' : 'text.secondary', mt: -0.5, mr: -0.5 }}
                                                        >
                                                            {entry.is_favorite ? <StarRoundedIcon fontSize="small" /> : <StarBorderRoundedIcon fontSize="small" />}
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            )}

                                            {entry.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mt: 0.5,
                                                        minWidth: 0,
                                                        overflowWrap: 'anywhere',
                                                        wordBreak: 'break-word',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {entry.description}
                                                </Typography>
                                            )}

                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {dateLabel}
                                                </Typography>
                                                {timeLabel && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        • {timeLabel}
                                                    </Typography>
                                                )}
                                            </Stack>

                                            {entry.tags && entry.tags.length > 0 && (
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                                    {entry.tags.map((tag) => (
                                                        <TagChip key={tag.id} tag={tag} />
                                                    ))}
                                                </Stack>
                                            )}

                                            {entry.calendar_type === 'event' && (counts.going !== null
                                                || counts.notGoing !== null
                                                || counts.maybe !== null
                                                || counts.comments !== null
                                                || counts.media !== null) && (
                                                <>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                                        {counts.going !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="filled"
                                                                icon={<CheckCircleOutlineIcon fontSize="small" />}
                                                                label={counts.going}
                                                                sx={RSVP_CHIP_SX.going}
                                                            />
                                                        )}
                                                        {counts.notGoing !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="filled"
                                                                icon={<CancelOutlinedIcon fontSize="small" />}
                                                                label={counts.notGoing}
                                                                sx={RSVP_CHIP_SX.notGoing}
                                                            />
                                                        )}
                                                        {counts.maybe !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="filled"
                                                                icon={<HelpOutlineIcon fontSize="small" />}
                                                                label={counts.maybe}
                                                                sx={RSVP_CHIP_SX.maybe}
                                                            />
                                                        )}
                                                        {counts.comments !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                icon={<ChatBubbleOutlineIcon fontSize="small" />}
                                                                label={counts.comments}
                                                            />
                                                        )}
                                                        {counts.media !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                icon={<PhotoLibraryOutlinedIcon fontSize="small" />}
                                                                label={counts.media}
                                                            />
                                                        )}
                                                    </Stack>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}

                            {showPastMobile && mobilePastEntries.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, pt: 0.5 }}>
                                    Vergangene Termine
                                </Typography>
                            )}
                            {showPastMobile && mobilePastEntries.map((entry) => {
                                const start = parseLocalDateTime(entry.start_time);
                                const end = parseLocalDateTime(entry.end_time);
                                const dateLabel = formatMobileEventDate(start, end);
                                const timeLabel = formatMobileEventTime(entry, start, end);
                                const counts = extractEventCounts(entry);
                                const bookingTitleParts = splitBookingTitleForMobile(entry.title);

                                return (
                                    <Card
                                        key={entry.id}
                                        variant="outlined"
                                        sx={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            minWidth: 0,
                                            borderRadius: 2,
                                            borderLeft: '4px solid',
                                            borderLeftColor: entry.calendar_type === 'event' ? CAL_EVENT_ACCENT : CAL_BOOKING_ACCENT,
                                            boxShadow: '0px 10px 26px rgba(15, 23, 42, 0.08)',
                                        }}
                                    >
                                        <CardContent
                                            sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, cursor: 'pointer', minWidth: 0 }}
                                            onClick={() => entry.id !== undefined && openDetail(entry.id)}
                                        >
                                            {entry.calendar_type === 'booking' && bookingTitleParts.secondary ? (
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 700,
                                                            minWidth: 0,
                                                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {bookingTitleParts.primary}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            minWidth: 0,
                                                            mt: 0.125,
                                                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {bookingTitleParts.secondary}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                                    <Typography
                                                        variant="subtitle1"
                                                        noWrap={!isMobile}
                                                        sx={{
                                                            fontWeight: 700,
                                                            minWidth: 0,
                                                            flex: 1,
                                                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: { xs: '-webkit-box', md: 'block' },
                                                            WebkitLineClamp: { xs: 2, md: 'unset' },
                                                            WebkitBoxOrient: { xs: 'vertical', md: 'unset' },
                                                        }}
                                                    >
                                                        {entry.title}
                                                    </Typography>
                                                    {entry.calendar_type === 'event' && (
                                                        <IconButton
                                                            size="small"
                                                            aria-label={entry.is_favorite ? 'aus Favoriten entfernen' : 'zu Favoriten hinzufügen'}
                                                            onClick={(eventClick) => {
                                                                eventClick.stopPropagation();
                                                                void handleToggleFavorite(entry.id as number, !Boolean(entry.is_favorite));
                                                            }}
                                                            sx={{ color: entry.is_favorite ? 'var(--pvta-favorite)' : 'text.secondary', mt: -0.5, mr: -0.5 }}
                                                        >
                                                            {entry.is_favorite ? <StarRoundedIcon fontSize="small" /> : <StarBorderRoundedIcon fontSize="small" />}
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            )}

                                            {entry.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mt: 0.5,
                                                        minWidth: 0,
                                                        overflowWrap: 'anywhere',
                                                        wordBreak: 'break-word',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {entry.description}
                                                </Typography>
                                            )}

                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {dateLabel}
                                                </Typography>
                                                {timeLabel && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        • {timeLabel}
                                                    </Typography>
                                                )}
                                            </Stack>

                                            {entry.tags && entry.tags.length > 0 && (
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                                    {entry.tags.map((tag) => (
                                                        <TagChip key={tag.id} tag={tag} />
                                                    ))}
                                                </Stack>
                                            )}

                                            {entry.calendar_type === 'event' && (counts.going !== null
                                                || counts.notGoing !== null
                                                || counts.maybe !== null
                                                || counts.comments !== null
                                                || counts.media !== null) && (
                                                <>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                                        {counts.going !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="filled"
                                                                icon={<CheckCircleOutlineIcon fontSize="small" />}
                                                                label={counts.going}
                                                                sx={RSVP_CHIP_SX.going}
                                                            />
                                                        )}
                                                        {counts.notGoing !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="filled"
                                                                icon={<CancelOutlinedIcon fontSize="small" />}
                                                                label={counts.notGoing}
                                                                sx={RSVP_CHIP_SX.notGoing}
                                                            />
                                                        )}
                                                        {counts.maybe !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="filled"
                                                                icon={<HelpOutlineIcon fontSize="small" />}
                                                                label={counts.maybe}
                                                                sx={RSVP_CHIP_SX.maybe}
                                                            />
                                                        )}
                                                        {counts.comments !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                icon={<ChatBubbleOutlineIcon fontSize="small" />}
                                                                label={counts.comments}
                                                            />
                                                        )}
                                                        {counts.media !== null && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                icon={<PhotoLibraryOutlinedIcon fontSize="small" />}
                                                                label={counts.media}
                                                            />
                                                        )}
                                                    </Stack>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}

                    {!isMobile && (
                        <Box
                            sx={{
                                minWidth: 0,
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                "& .fc": {
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: "0px 16px 32px rgba(15, 23, 42, 0.08)",
                                    padding: { xs: 1, md: 2 },
                                },
                                "& .fc .fc-toolbar": {
                                    marginBottom: 2,
                                    gap: 1,
                                },
                                "& .fc .fc-toolbar-title": {
                                    fontSize: '1.3rem',
                                    fontWeight: 700,
                                    color: 'text.primary',
                                },
                                "& .fc .fc-button": {
                                    backgroundColor: '#f4f1ec',
                                    color: '#1f2328',
                                    border: '1px solid #e2dcd3',
                                    borderRadius: 999,
                                    padding: '6px 14px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    transition: 'transform 200ms ease, box-shadow 200ms ease, background-color 200ms ease',
                                },
                                "& .fc .fc-button:hover": {
                                    backgroundColor: '#ece7df',
                                    boxShadow: '0px 8px 18px rgba(15, 23, 42, 0.12)',
                                    transform: 'translateY(-1px)',
                                },
                                "& .fc .fc-button-primary:not(:disabled).fc-button-active": {
                                    backgroundColor: GREEN_700,
                                    borderColor: GREEN_700,
                                    color: '#ffffff',
                                },
                                "& .fc .fc-button:disabled": {
                                    backgroundColor: '#f7f4ef',
                                    color: '#a3a3a3',
                                },
                                "& .fc .fc-col-header-cell": {
                                    backgroundColor: '#f4f1ec',
                                    borderBottom: '1px solid #e6e1d9',
                                },
                                "& .fc .fc-col-header-cell-cushion": {
                                    padding: '10px 0',
                                    fontWeight: 600,
                                    color: '#30363d',
                                },
                                "& .fc .fc-daygrid-day": {
                                    transition: 'background-color 200ms ease',
                                },
                                "& .fc .fc-daygrid-day-frame": {
                                    padding: '6px',
                                },
                                "& .fc .fc-daygrid-day:hover": {
                                    backgroundColor: 'rgba(18, 61, 36, 0.06)',
                                },
                                "& .fc .fc-daygrid-event": {
                                    borderRadius: 10,
                                    padding: '4px 8px',
                                    boxShadow: '0px 6px 14px rgba(15, 23, 42, 0.12)',
                                    fontWeight: 600,
                                },
                                "& .fc .fc-timegrid-slot": {
                                    borderColor: '#eee7de',
                                },
                                "& .fc .fc-timegrid-axis": {
                                    color: '#6b727c',
                                },
                                "& .fc .fc-timegrid-event": {
                                    borderRadius: 10,
                                    padding: '6px 8px',
                                    boxShadow: '0px 8px 16px rgba(15, 23, 42, 0.12)',
                                },
                            }}
                        >
                            <FullCalendar
                                plugins={[dayGridPlugin, interactionPlugin]}
                                initialView="dayGridMonth"
                                events={events}
                                locale={deLocale}
                                eventClick={handleEventClick}
                                dateClick={handleDateClick}
                                height="760px"
                                headerToolbar={{
                                    left: 'prev,next today',
                                    center: 'title',
                                    right: 'dayGridMonth,dayGridWeek,dayGridDay',
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Container>

            {dialogEntry && (
                <CalendarEventEdit
                    event={dialogEntry}
                    onClose={handleCloseDialog}
                    onSave={(entry) => updateCalendarEntry.mutate(entry, { onSuccess: handleCloseDialog })}
                    onDelete={(id) => deleteCalendarEntry.mutate(id, { onSuccess: handleCloseDialog })}
                    onToggleFavorite={handleToggleFavorite}
                    isFavoritePending={toggleFavoriteMutation.isPending}
                />
            )}
        </Box>
    );
};

export default Calendar;
